import React, { useState, useContext, useEffect, useRef } from "react";
import { GlobalContext } from "../../../App";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ToastContainer, toast } from "react-toastify";
import { HOST, HOST_MEDIA_URL } from "../../host"; 

//Production URL 
const NDVI_FILE = `${HOST_MEDIA_URL}/media/Hydenment/Farm_NDVI.geojson`;     

//Local Environment Setup
// const NDVI_FILE = `/media/Hydenment/Farm_NDVI.geojson`;

const KEY_PREFIX = "NDVI_";

function getNdviColor(avg) {
  if (avg <= -0.1)               return "#d73027";
  if (avg > -0.1 && avg <= 0)   return "#fc8d59";
  if (avg > 0    && avg <= 0.2) return "#ffff00";
  if (avg > 0.2  && avg <= 0.4) return "#90ee90";
  return "#00aa00";
}

export default function Hydenment() {
  const {
    map,
    setChart,
    setChartType,
    setXProperty,
    setYProperty,
    setXValues,
    setYValues,
  } = useContext(GlobalContext);

  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const geoJsonLayerRef = useRef(null);

  const ctxRef = useRef({});
  useEffect(() => {
    ctxRef.current = { setChart, setChartType, setXProperty, setYProperty, setXValues, setYValues };
  }, [setChart, setChartType, setXProperty, setYProperty, setXValues, setYValues]);

  const removeLayer = () => {
    if (geoJsonLayerRef.current) {
      try { geoJsonLayerRef.current.remove(); } catch (_) {}
      geoJsonLayerRef.current = null;
    }
  };

  const handleFeatureClick = (feature) => {
    const {
      setChart, setChartType,
      setXProperty, setYProperty,
      setXValues, setYValues,
    } = ctxRef.current;

    const props = feature.properties ?? {};

    // Parse all NDVI_DD.MM.YY keys, sort chronologically
    const series = Object.entries(props)
      .filter(([k]) => k.startsWith(KEY_PREFIX))
      .map(([k, v]) => {
        const datePart = k.replace(KEY_PREFIX, ""); // "24.11.24"
        const [dd, mm, yy] = datePart.split(".");
        const fullYear = parseInt(yy, 10) >= 50 ? `19${yy}` : `20${yy}`;
        const dateObj = new Date(`${fullYear}-${mm}-${dd}`);
        return {
          dateObj,
          dateStr: `${dd}/${mm}/${fullYear}`,
          value: parseFloat(v),
        };
      })
      .filter(({ dateObj, value }) => !isNaN(dateObj.getTime()) && !isNaN(value))
      .sort((a, b) => a.dateObj - b.dateObj);

    if (!series.length) {
      toast.warning("No NDVI data for this farm.");
      return;
    }

    const dates  = series.map((s) => s.dateStr);
    const values = series.map((s) => s.value);

    setXProperty("Date");
    setYProperty("NDVI Value");
    setXValues(dates);
    setYValues(values);

    // chartType must be "ndvi_line" so NDVIChart renders
    setChartType("ndvi_line");
    setChart({
      dates,
      values,
      farmId: props.FARM_ID_ ?? props.OBJECTID ?? "Unknown",
      chartType: "ndvi_line",
      featureProperties: props,
    });
  };

  const loadNdvi = async () => {
    if (!map) { toast.error("Map not ready."); return; }
    setLoading(true);
    try {
      const res = await fetch(NDVI_FILE);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const geojsonData = await res.json();

      removeLayer();

      const layer = L.geoJSON(geojsonData, {
        style: (feature) => {
          const vals = Object.entries(feature.properties)
            .filter(([k]) => k.startsWith(KEY_PREFIX))
            .map(([, v]) => parseFloat(v))
            .filter((v) => !isNaN(v));
          const avg = vals.length
            ? vals.reduce((a, b) => a + b, 0) / vals.length
            : 0;
          return {
            color: "#333",
            weight: 1.5,
            opacity: 0.8,
            fillOpacity: 0.6,
            fillColor: getNdviColor(avg),
          };
        },
        onEachFeature: (feature, lyr) => {
          lyr.on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            handleFeatureClick(feature);
          });
        },
      }).addTo(map);

      geoJsonLayerRef.current = layer;

      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { padding: [40, 40], duration: 1.0 });
      }
    } catch (err) {
      toast.error(`Failed to load NDVI: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (active) {
      removeLayer();
      setActive(false);
    } else {
      setActive(true);
      loadNdvi();
    }
  };

  useEffect(() => () => removeLayer(), []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <button
        onClick={handleToggle}
        title="NDVI Farm Analysis"
        style={{
          zIndex: 1000,
          fontSize: "15px",
          padding: "2px",
          width: "38px",
          height: "38px",
          borderRadius: "50%",
          backgroundColor: active ? "#2E7D32" : "black",
          border: active ? "2px solid #4CAF50" : "none",
          color: "white",
          cursor: loading ? "wait" : "pointer",
          transition: "all 0.2s",
          opacity: loading ? 0.7 : 1,
        }}
      >
        <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-leaf"}`} />
      </button>
    </div>
  );
}